import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon, Button, Badge } from '@/components/atoms';
import { useGRNDetail } from '@/hooks/useErpApi';
import type { GRNItem } from '@/schemas/grn';

interface GRNDetailProps {
  grnId: string;
}

export const GRNDetail: React.FC<GRNDetailProps> = ({ grnId }) => {
  const navigate = useNavigate();
  const { data: grn, isLoading, error, refetch } = useGRNDetail(grnId);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status: 'accepted' | 'rejected' | 'partial') => {
    switch (status) {
      case 'accepted':
        return <Badge variant="success">Accepted</Badge>;
      case 'rejected':
        return <Badge variant="error">Rejected</Badge>;
      case 'partial':
        return <Badge variant="warning">Partial</Badge>;
    }
  };

  const calculateTotals = (items: GRNItem[]) => {
    return items.reduce(
      (acc, item) => ({
        ordered: acc.ordered + item.ordered_quantity,
        received: acc.received + item.received_quantity,
        accepted: acc.accepted + item.accepted_quantity,
        rejected: acc.rejected + item.rejected_quantity,
      }),
      { ordered: 0, received: 0, accepted: 0, rejected: 0 }
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <Icon name="Package" size={48} className="text-neutral-400 mx-auto mb-4" />
          <p className="text-lg font-medium text-neutral-600">Loading GRN details...</p>
        </div>
      </div>
    );
  }

  if (error || !grn) {
    return (
      <div className="p-8">
        <div className="bg-error-50 border border-error-200 rounded-lg p-6">
          <div className="flex items-start gap-4">
            <div className="bg-error-100 p-2 rounded-lg">
              <Icon name="CircleAlert" size={24} className="text-error-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-error-900 mb-1">
                Failed to Load GRN Details
              </h3>
              <p className="text-error-700 mb-3">Unable to fetch GRN data. Please try again.</p>
              <Button variant="primary" size="sm" onClick={() => refetch()}>
                <Icon name="RefreshCw" size={16} className="mr-2" />
                Retry
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const totals = calculateTotals(grn.items);

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl font-bold text-neutral-900">{grn.grn_number}</h1>
              {getStatusBadge(grn.quality_status as 'accepted' | 'rejected' | 'partial')}
            </div>
            <p className="text-neutral-600">Goods Receipt Note Details</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(`/purchase-orders/${grn.po_id}`)}
            >
              <Icon name="FileText" size={16} className="mr-2" />
              View PO
            </Button>
            <Button variant="ghost" size="sm" onClick={() => window.print()}>
              <Icon name="Printer" size={16} className="mr-2" />
              Print
            </Button>
          </div>
        </div>

        {/* GRN Info Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
          <div>
            <p className="text-sm text-neutral-600 mb-1">Purchase Order</p>
            <button
              onClick={() => navigate(`/purchase-orders/${grn.po_id}`)}
              className="text-base font-semibold text-primary-600 hover:text-primary-700 hover:underline"
            >
              {grn.po_number}
            </button>
          </div>
          <div>
            <p className="text-sm text-neutral-600 mb-1">Warehouse</p>
            <p className="text-base font-semibold text-neutral-900">{grn.warehouse_name}</p>
          </div>
          <div>
            <p className="text-sm text-neutral-600 mb-1">Received Date</p>
            <p className="text-base font-semibold text-neutral-900">
              {formatDate(grn.received_date)}
            </p>
          </div>
          <div>
            <p className="text-sm text-neutral-600 mb-1">Received By</p>
            <p className="text-base font-semibold text-neutral-900">{grn.received_by}</p>
          </div>
          <div>
            <p className="text-sm text-neutral-600 mb-1">Created At</p>
            <p className="text-base font-semibold text-neutral-900">{formatDate(grn.created_at)}</p>
          </div>
          {(grn as any).documents && (grn as any).documents.length > 0 && (
            <div>
              <p className="text-sm text-neutral-600 mb-1">Documents</p>
              <div className="flex flex-col gap-2">
                {(grn as any).documents.map((url: string, index: number) => {
                  const fileName = url.split('/').pop()?.split('?')[0] || `Document ${index + 1}`;
                  return (
                    <a
                      key={index}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-base font-semibold text-primary-600 hover:text-primary-700 hover:underline inline-flex items-center gap-1"
                    >
                      <Icon name="FileText" size={16} />
                      {decodeURIComponent(fileName)}
                    </a>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Remarks */}
        {grn.remarks && (
          <div className="mt-6 p-4 bg-neutral-50 rounded-lg border border-neutral-200">
            <p className="text-sm font-semibold text-neutral-700 mb-2">Quality Inspection Notes</p>
            <p className="text-neutral-900">{grn.remarks}</p>
          </div>
        )}
      </div>

      {/* Items Section */}
      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
        <h2 className="text-xl font-bold text-neutral-900 mb-4">Received Items</h2>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-neutral-200">
                <th className="text-left py-3 px-4 text-sm font-semibold text-neutral-700">
                  Product
                </th>
                <th className="text-center py-3 px-4 text-sm font-semibold text-neutral-700">
                  Ordered
                </th>
                <th className="text-center py-3 px-4 text-sm font-semibold text-neutral-700">
                  Received
                </th>
                <th className="text-center py-3 px-4 text-sm font-semibold text-neutral-700">
                  Accepted
                </th>
                <th className="text-center py-3 px-4 text-sm font-semibold text-neutral-700">
                  Rejected
                </th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-neutral-700">
                  Batch Number
                </th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-neutral-700">
                  Expiry Date
                </th>
              </tr>
            </thead>
            <tbody>
              {grn.items.map((item) => (
                <tr key={item.id} className="border-b border-neutral-100">
                  <td className="py-3 px-4">
                    <div>
                      <p className="font-medium text-neutral-900">{item.product_name}</p>
                      <p className="text-sm text-neutral-600">{item.product_sku}</p>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-center">
                    <span className="text-neutral-700">{item.ordered_quantity}</span>
                  </td>
                  <td className="py-3 px-4 text-center">
                    <span className="text-neutral-900 font-medium">{item.received_quantity}</span>
                  </td>
                  <td className="py-3 px-4 text-center">
                    <span className="text-success-700 font-medium">{item.accepted_quantity}</span>
                  </td>
                  <td className="py-3 px-4 text-center">
                    {item.rejected_quantity > 0 ? (
                      <span className="text-error-700 font-medium">{item.rejected_quantity}</span>
                    ) : (
                      <span className="text-neutral-400">0</span>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-neutral-700">{item.batch_number || '-'}</span>
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-neutral-700">
                      {new Date(item.expiry_date).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      })}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-neutral-300 font-semibold bg-neutral-50">
                <td className="py-3 px-4 text-neutral-900">Total</td>
                <td className="py-3 px-4 text-center text-neutral-900">{totals.ordered}</td>
                <td className="py-3 px-4 text-center text-neutral-900">{totals.received}</td>
                <td className="py-3 px-4 text-center text-success-700">{totals.accepted}</td>
                <td className="py-3 px-4 text-center text-error-700">{totals.rejected}</td>
                <td className="py-3 px-4" colSpan={2}></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Inventory Batches Section */}
      {grn.items.some((item) => item.inventory_batch_id) && (
        <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
          <h2 className="text-xl font-bold text-neutral-900 mb-4">Created Inventory Batches</h2>
          <div className="space-y-2">
            {grn.items
              .filter((item) => item.inventory_batch_id)
              .map((item) => (
                <div
                  key={item.id}
                  className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg border border-neutral-200"
                >
                  <div>
                    <p className="font-medium text-neutral-900">{item.product_name}</p>
                    <p className="text-sm text-neutral-600">
                      Batch: {item.batch_number} | Quantity: {item.accepted_quantity}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate(`/batches?batch_id=${item.inventory_batch_id}`)}
                  >
                    <Icon name="Eye" size={16} className="mr-1" />
                    View Batch
                  </Button>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};
